import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { BaseService } from './base.service';
import { GlobalService } from './global.service';
import { ConnMessageService } from './conn-message.service';
import { HttpClient } from '@angular/common/http';
import { CreateSupportLogRequest } from '../_models/support/create-support-log-request';

/**
 * SupportService
 * 
 * A dedicated service for handling support requests and communications with the support team.
 * This service centralizes all support-related operations including:
 * 
 * - General support requests
 * - Addon inquiries
 * - Hardware catalog requests
 * - Expert consultation requests
 * - Custom support messages
 * 
 * Features:
 * - Standardized request format
 * - Automatic user context inclusion
 * - Success/error message handling
 * - Type-safe request interfaces
 * 
 * @author Support Team
 * @version 1.0.0
 */

// Interfaces for different types of support requests
export interface SupportRequestBase {
  userEmail: string;
  companyName: string;
  question: string;
  message: string;
}

export interface GeneralSupportRequest extends SupportRequestBase {
  type: 'general';
}

export interface AddonSupportRequest extends SupportRequestBase {
  type: 'addon';
  requestedAddons?: string[];
}

export interface HardwareCatalogRequest extends SupportRequestBase {
  type: 'hardware';
}

export interface ExpertConsultationRequest extends SupportRequestBase {
  type: 'expert';
  consultationArea?: string;
}

export type SupportRequest = 
  | GeneralSupportRequest 
  | AddonSupportRequest 
  | HardwareCatalogRequest 
  | ExpertConsultationRequest;

export interface SupportRequestOptions {
  showSuccessMessage?: boolean;
  successMessage?: string;
  showSpinner?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class SupportService {
  private readonly SUPPORT_ENDPOINT = 'EmailForAddonsPlan';

  constructor(
    private _baseService: BaseService,
    private _connMessageService: ConnMessageService,
    private _http:HttpClient,
    
  ) {}

  /**
   * Sends a general support request to the support team
   * @param question - The subject/question for the support request
   * @param message - The detailed message content
   * @param options - Optional configuration for the request
   * @returns Observable with the response
   */
  sendGeneralSupportRequest(
    question: string, 
    message: string, 
    options: SupportRequestOptions = {}
  ): Observable<any> {
    const request: GeneralSupportRequest = {
      type: 'general',
      userEmail: this.getUserEmail(),
      companyName: this.getCompanyName(),
      question,
      message
    };

    return this.sendSupportRequest(request, options);
  }

  /**
   * Sends an addon inquiry request to the support team
   * @param question - The subject/question about addons
   * @param message - The detailed message content
   * @param requestedAddons - Optional array of specific addons being inquired about
   * @param options - Optional configuration for the request
   * @returns Observable with the response
   */
  sendAddonInquiry(
    question: string, 
    message: string, 
    requestedAddons: string[] = [], 
    options: SupportRequestOptions = {}
  ): Observable<any> {
    const request: AddonSupportRequest = {
      type: 'addon',
      userEmail: this.getUserEmail(),
      companyName: this.getCompanyName(),
      question,
      message,
      requestedAddons
    };

    return this.sendSupportRequest(request, options);
  }

  /**
   * Sends a hardware catalog request to the support team
   * @param options - Optional configuration for the request
   * @returns Observable with the response
   */
  sendHardwareCatalogRequest(options: SupportRequestOptions = {}): Observable<any> {
    const request: HardwareCatalogRequest = {
      type: 'hardware',
      userEmail: this.getUserEmail(),
      companyName: this.getCompanyName(),
      question: 'Request for Hardware Catalog',
      message: 'Contact me regarding hardware catalog details'
    };

    const defaultOptions: SupportRequestOptions = {
      showSuccessMessage: true,
      successMessage: 'Thank you! Your hardware catalog request has been sent to our support team.',
      ...options
    };

    return this.sendSupportRequest(request, defaultOptions);
  }

  /**
   * Sends an expert consultation request to the support team
   * @param question - The subject/question for expert consultation
   * @param message - The detailed message content
   * @param consultationArea - Optional specific area of expertise needed
   * @param options - Optional configuration for the request
   * @returns Observable with the response
   */
  sendExpertConsultationRequest(
    question: string, 
    message: string, 
    consultationArea?: string,
    options: SupportRequestOptions = {}
  ): Observable<any> {
    const request: ExpertConsultationRequest = {
      type: 'expert',
      userEmail: this.getUserEmail(),
      companyName: this.getCompanyName(),
      question,
      message,
      consultationArea
    };

    const defaultOptions: SupportRequestOptions = {
      showSuccessMessage: true,
      successMessage: 'Thank you! Your expert consultation request has been sent to our team.',
      ...options
    };

    return this.sendSupportRequest(request, defaultOptions);
  }

  /**
   * Sends a payment processing setup support request
   * @param question - The subject/question for payment processing
   * @param message - The detailed message content with business information
   * @param options - Optional configuration for the request
   * @returns Observable with the response
   */
  sendPaymentProcessingSetupRequest(
    question: string, 
    message: string, 
    options: SupportRequestOptions = {}
  ): Observable<any> {
    const request: GeneralSupportRequest = {
      type: 'general',
      userEmail: this.getUserEmail(),
      companyName: this.getCompanyName(),
      question,
      message: `Payment Processing Setup Request:\n\n${message}`
      
    };

    const defaultOptions: SupportRequestOptions = {
      showSuccessMessage: false, // Let calling component handle messages
      successMessage: '',
      ...options
    };

    return this.sendSupportRequest(request, defaultOptions);
  }

  /**
   * Sends a custom support request with full control over the request data
   * @param request - The complete support request object
   * @param options - Optional configuration for the request
   * @returns Observable with the response
   */
  sendCustomSupportRequest(
    request: Partial<SupportRequestBase>, 
    options: SupportRequestOptions = {}
  ): Observable<any> {
    const completeRequest: SupportRequestBase = {
      userEmail: request.userEmail || this.getUserEmail(),
      companyName: request.companyName || this.getCompanyName(),
      question: request.question || '',
      message: request.message || '',
      ...request
    };

    return this.sendSupportRequest(completeRequest, options);
  }

  /**
   * Core method to send support requests to the API
   * @param request - The support request data
   * @param options - Configuration options for the request
   * @returns Observable with the response
   */
  private sendSupportRequest(
    request: SupportRequestBase | SupportRequest, 
    options: SupportRequestOptions = {}
  ): Observable<any> {
    const url = environment.BaseURL + this.SUPPORT_ENDPOINT;
    
    const defaultOptions: SupportRequestOptions = {
      showSuccessMessage: true,
      successMessage: 'Thanks! Your message has been emailed to our support team. We will follow up shortly at your provided email.',
      showSpinner: false
    };

    const finalOptions = { ...defaultOptions, ...options };

    return this._baseService.httpPost(url, request).pipe(
      map((response: any) => {
        if (response.success && finalOptions.showSuccessMessage) {
          this._connMessageService.showToastMessage(
            finalOptions.successMessage!,
            'success'
          );
        }
        return response;
      })
    );
  }

  /**
   * Gets the current user's email address
   * @returns User email or empty string if not available
   */
  private getUserEmail(): string {
    try {
      return GlobalService.userInfo?.email || '';
    } catch (error) {
      console.warn('Unable to get user email:', error);
      return '';
    }
  }

  /**
   * Gets the current company name
   * @returns Company name or empty string if not available
   */
  private getCompanyName(): string {
    try {
      return GlobalService.userInfo?.companyName || '';
    } catch (error) {
      console.warn('Unable to get company name:', error);
      return '';
    }
  }

  /**
   * Validates if the required user information is available
   * @returns True if user info is available, false otherwise
   */
  isUserInfoAvailable(): boolean {
    return !!(this.getUserEmail() && this.getCompanyName());
  }

  /**
   * Gets formatted user context for debugging or logging
   * @returns Object with user context information
   */
  getUserContext(): { email: string; companyName: string; hasInfo: boolean } {
    return {
      email: this.getUserEmail(),
      companyName: this.getCompanyName(),
      hasInfo: this.isUserInfoAvailable()
    };
  }

  createLog(body: CreateSupportLogRequest): Observable<any> {
    // id is optional; backend can generate Guid
    const payload = {
      status: 'pending',
      ...body,
    };
    // Temporarily disable support log creation (GetOrderByInvoiceNoCreateSupportLog)
    return new Observable<any>((observer) => {
      observer.next({ success: true, message: 'Support log disabled' });
      observer.complete();
    });
  }
}
